import { useEffect, useState } from 'react';
import { message } from 'antd';

import { fetchStaffSwapData, postStaffSwap } from './staffSwapApi';

export const SWAP_REFRESH_WARNING = "Staff swapped, but the latest allocation data couldn't be refreshed.";

/**
 * Picker state and the swap request lifecycle shared by every Swap entry point.
 * Pages supply only what differs: how to patch their own state and how to refresh.
 *
 * - applySwap(responseData, source, replacement): patch page state from the authoritative response.
 * - refreshAfterSwap(setupId): reload page data; resolve true when everything refreshed.
 * - setRefreshWarning(text): show or clear the page's post-mutation warning.
 * - mutationRef: the page's ref shared with its other staff mutations so they never overlap.
 */
export default function useStaffSwap({
  getSetupApiPath,
  selectedSetupId,
  selectedSetupIdRef,
  applySwap,
  refreshAfterSwap,
  setRefreshWarning,
  mutationRef,
}) {
  const [swapSource, setSwapSource] = useState(null);
  const [swapping, setSwapping] = useState(false);

  useEffect(() => {
    setSwapSource(null);
    setSwapping(false);
  }, [selectedSetupId]);

  const loadSwapData = () => fetchStaffSwapData(getSetupApiPath);

  // Errors propagate so the picker can show them and refresh on a 409.
  const swapStaff = async (source, replacement) => {
    if (mutationRef.current) throw new Error('Another staff change is in progress');
    mutationRef.current = true;
    const operationSetupId = selectedSetupId;
    setSwapping(true);
    setRefreshWarning('');
    try {
      const response = await postStaffSwap(getSetupApiPath, source, replacement);
      message.success(response.data.message);
      if (selectedSetupIdRef.current !== operationSetupId) return;

      applySwap(response.data, source, replacement);
      setSwapSource(null);
      const refreshed = await refreshAfterSwap(operationSetupId);
      if (selectedSetupIdRef.current === operationSetupId && !refreshed) {
        setRefreshWarning(SWAP_REFRESH_WARNING);
      }
    } finally {
      mutationRef.current = false;
      if (selectedSetupIdRef.current === operationSetupId) setSwapping(false);
    }
  };

  return {
    swapSource,
    openSwap: setSwapSource,
    closeSwap: () => setSwapSource(null),
    swapping,
    loadSwapData,
    swapStaff,
  };
}
